import { useEffect, useState } from "react";
import { cx } from "@/components/ui";

/** « Purple Store » → « PS », « Green Corner » → « GC », « Nantes » → « NA ». */
export function networkInitials(name: string): string {
  const words = name.trim().split(/[\s\-_']+/).filter(Boolean);
  if (words.length === 0) return "P";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Le logo d'un réseau (Paramètres → Personnalisation), sinon ses initiales.
 * `tone` : sur la barre latérale sombre ou sur fond clair.
 */
export function NetworkLogo({
  name,
  logoUrl,
  size = 38,
  tone = "dark",
  className,
}: {
  name: string;
  logoUrl: string | null | undefined;
  size?: number;
  tone?: "dark" | "light";
  className?: string;
}) {
  const [broken, setBroken] = useState<string | null>(null);
  const box = { width: size, height: size };
  if (logoUrl && broken !== logoUrl) {
    return (
      <div style={box} className={cx("flex flex-shrink-0 items-center justify-center overflow-hidden rounded-[11px] bg-white p-[3px]", className)}>
        <img src={logoUrl} alt={name} className="max-h-full max-w-full object-contain" onError={() => setBroken(logoUrl)} />
      </div>
    );
  }
  return (
    <div
      style={{ ...box, fontSize: Math.round(size * 0.42) }}
      className={cx(
        "flex flex-shrink-0 items-center justify-center rounded-[11px] font-display font-extrabold",
        tone === "dark" ? "bg-[oklch(0.98_0.01_150/0.15)] text-white" : "bg-sidebar text-white",
        className
      )}
    >
      {networkInitials(name)}
    </div>
  );
}

/**
 * Forme du logo une fois chargé : « wide » pour un logotype horizontal (plus
 * de 2 fois plus large que haut), qui s'affiche seul à la place du nom.
 */
export function useLogoShape(logoUrl: string | null | undefined): "wide" | "square" | null {
  const [shape, setShape] = useState<{ url: string; wide: boolean } | null>(null);
  useEffect(() => {
    if (!logoUrl) return;
    const img = new Image();
    img.onload = () => setShape({ url: logoUrl, wide: img.naturalWidth > img.naturalHeight * 2 });
    img.src = logoUrl;
    return () => {
      img.onload = null;
    };
  }, [logoUrl]);
  if (!logoUrl || shape?.url !== logoUrl) return null;
  return shape.wide ? "wide" : "square";
}
